import type { Row } from '@tanstack/react-table';
import { MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@ui/dropdown-menu.tsx';
import { Button } from '@r/components/ui/button.tsx';
import { useTranslation } from 'react-i18next';
import { Plus, RotateCcw } from 'lucide-react';
import { Pencil2Icon } from '@radix-ui/react-icons';

interface LocaleRowData {
  id?: number;
  isCustom?: boolean;
  isEditable?: boolean;
}

interface DataTableRowActionsProps<TData extends LocaleRowData> {
  row: Row<TData>;
  isUserAdmin: boolean;
  onEditCustom: (value: TData) => void;
  onCreateCustom: (value: TData) => void;
  onRollbackCustom: (value: TData) => void;
}

const DataTableRowActions = <TData extends LocaleRowData>({
  row,
  isUserAdmin,
  onEditCustom,
  onCreateCustom,
  onRollbackCustom
}: DataTableRowActionsProps<TData>) => {
  const { t } = useTranslation();

  const locale = row.original;
  const isCustom = locale.isCustom ?? false;
  const isEditable = locale.isEditable ?? false;

  const canEditCustom = isCustom && isEditable && isUserAdmin;
  const canCreateCustom = !isCustom && isEditable && isUserAdmin;
  const canRollbackCustom = isCustom && isUserAdmin;

  const hasAnyAction = canEditCustom || canCreateCustom || canRollbackCustom;

  if (!hasAnyAction) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="flex h-8 w-8 p-0 data-[state=open]:bg-muted float-end">
          <MoreHorizontal className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {canEditCustom && (
          <DropdownMenuItem onClick={() => onEditCustom(locale)}>
            <Pencil2Icon className="w-4 h-4 mr-2" />
            {t('label.edit.custom.translation')}
          </DropdownMenuItem>
        )}

        {canCreateCustom && (
          <DropdownMenuItem onClick={() => onCreateCustom(locale)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('label.create.custom.translation')}
          </DropdownMenuItem>
        )}

        {(canEditCustom || canCreateCustom) && canRollbackCustom && <DropdownMenuSeparator />}

        {canRollbackCustom && (
          <DropdownMenuItem onClick={() => onRollbackCustom(locale)} className="text-orange-600">
            <RotateCcw className="w-4 h-4 mr-2" />
            {t('label.rollback.custom.translation')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DataTableRowActions;
